import { Tooltip } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { Fragment, useEffect, useState } from 'react';

import { Button, Option, Text } from '@revelio/ui';

import { listDataBuilderDatasets } from '../api/save-dataset-api';
import { DataBuilderGrid } from '../components/ui/DataBuilderGrid';
import { DataBuilderOption } from '../components/ui/DataBuilderOption';
import { useDataBuilderNavigation } from '../useDataBuilderNavigation';

type DatasetStartMode = 'new' | 'saved';

type StartOption = {
  id: DatasetStartMode;
  title: string;
  detail: string;
  testId: string;
};

const START_OPTIONS: StartOption[] = [
  {
    id: 'new',
    title: 'New Datasets',
    detail: 'Build datasets from scratch using your selected sources',
    testId: 'step0-new-dataset',
  },
  {
    id: 'saved',
    title: 'Saved Datasets',
    detail: "Resume or modify existing datasets you've already created",
    testId: 'step0-saved-datasets',
  },
];

const SAVED_DATASETS_QUERY_KEY = ['data-builder-saved-datasets'];

const NO_SAVED_DATASETS_TOOLTIP = "You don't have any saved datasets yet";

export const DataBuilderChooseStep = () => {
  const [selectedMode, setSelectedMode] = useState<DatasetStartMode>('new');
  const { goToDatasetStep, goToSavedDatasetsStep } = useDataBuilderNavigation();

  const { data: savedDatasets, isSuccess: hasLoadedSavedDatasets } = useQuery({
    queryKey: SAVED_DATASETS_QUERY_KEY,
    queryFn: listDataBuilderDatasets,
  });

  const isSavedDisabled =
    hasLoadedSavedDatasets &&
    savedDatasets !== undefined &&
    savedDatasets.length === 0;

  useEffect(() => {
    if (isSavedDisabled && selectedMode === 'saved') {
      setSelectedMode('new');
    }
  }, [isSavedDisabled, selectedMode]);

  const handleContinue = () => {
    if (selectedMode === 'saved') {
      goToSavedDatasetsStep();
      return;
    }

    goToDatasetStep();
  };

  return (
    <section
      style={{
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '32px 24px',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '760px',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          alignItems: 'center',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Text
            as="h2"
            fontSize="lg"
            weight="semibold"
            style={{ textAlign: 'center' }}
          >
            Create Datasets
          </Text>
          <Text fontSize="md" style={{ textAlign: 'center' }}>
            Start with new datasets or continue working with ones you&apos;ve
            previously saved.
          </Text>
        </div>

        <DataBuilderGrid columns="2" style={{ width: '100%' }}>
          {START_OPTIONS.map((option) => {
            const isDisabled = option.id === 'saved' && isSavedDisabled;
            // `key` lives on whichever element this map callback returns —
            // either the bare <Option> or the wrapping <Tooltip>. Keeping it
            // off `optionEl` itself avoids two `key` props on the same render
            // path (React would only honor the outer one anyway).
            const optionEl = (
              <Option
                data-testid={option.testId}
                style={{ minHeight: '84px', alignItems: 'flex-start' }}
                isSelected={selectedMode === option.id}
                disabled={isDisabled}
                onClick={() => {
                  if (isDisabled) {
                    return;
                  }
                  setSelectedMode(option.id);
                }}
                startSlot={
                  <DataBuilderOption.Radio
                    isSelected={selectedMode === option.id}
                  />
                }
                startSlotAlignment="start"
                contentAlignment="start"
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                  }}
                >
                  <Text weight="semibold">{option.title}</Text>
                  <Text fontSize="x-sm" style={{ opacity: 0.75 }}>
                    {option.detail}
                  </Text>
                </div>
              </Option>
            );

            if (isDisabled) {
              return (
                <Tooltip
                  key={option.id}
                  label={NO_SAVED_DATASETS_TOOLTIP}
                  hasArrow
                  placement="top"
                  shouldWrapChildren
                >
                  {optionEl}
                </Tooltip>
              );
            }

            return <Fragment key={option.id}>{optionEl}</Fragment>;
          })}
        </DataBuilderGrid>

        <Button
          data-testid="step0-continue-button"
          color="primary"
          onClick={handleContinue}
          style={{ minWidth: '175px' }}
        >
          Create Datasets
        </Button>
      </div>
    </section>
  );
};
